import { ChevronIcon } from 'components/icons/ChevronIcon';
import { useLanguage } from 'context/language.context';
import { StringKey } from 'dictionaries/string-key.enum';
import { cn } from 'lib/utils';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

type SelectContextType<T> = {
  open: boolean;
  setOpen: (open: boolean) => void;
  value: T | null;
  setValue: (value: T) => void;
  onValueChange?: (value: T) => void;
  placeholder: ReactNode;
};

const SelectContext = createContext<SelectContextType<any> | null>(null);

type SelectProps<T = string> = {
  children: ReactNode;
  onValueChange?: (value: T) => void;
  value?: T;
  defaultValue?: T;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  chevronStyles?: string;
  errorMessage?: StringKey | string;
  label?: string;
};

export const Select = <T = string,>({
  children,
  onValueChange,
  value: controlledValue,
  defaultValue,
  open: controlledOpen,
  onOpenChange,
  className,
  errorMessage,
  label,
}: SelectProps<T>) => {
  const { t } = useLanguage();

  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const [uncontrolledValue, setUncontrolledValue] = useState<T | null>(defaultValue as T | null);

  const isControlled = controlledValue !== undefined;
  const open = controlledOpen !== undefined ? controlledOpen : uncontrolledOpen;
  const value = isControlled ? controlledValue : uncontrolledValue;

  const setOpen = useCallback(
    (newOpen: boolean) => {
      if (controlledOpen === undefined) {
        setUncontrolledOpen(newOpen);
      }
      onOpenChange?.(newOpen);
    },
    [controlledOpen, onOpenChange],
  );

  const setValue = useCallback(
    (newValue: T) => {
      if (!isControlled) {
        setUncontrolledValue(newValue);
      }
      onValueChange?.(newValue);
    },
    [isControlled, onValueChange],
  );

  return (
    <SelectContext.Provider
      value={{ open, setOpen, value, setValue, onValueChange, placeholder: null }}
    >
      <div className={cn('relative font-medium', className)}>
        {label && <label className="relative text-sm md:text-m -top-1">{label}</label>}
        <div className="mt-1">{children}</div>
        {errorMessage && (
          <p className="w-0 min-w-[calc(100%-32px)] text-error text-sm font-light mt-3 mx-4">
            {t(errorMessage as StringKey) ?? errorMessage}
          </p>
        )}
      </div>
    </SelectContext.Provider>
  );
};

export const SelectTrigger = React.forwardRef<
  HTMLButtonElement,
  Omit<React.ComponentProps<'button'>, 'className'> & {
    className?: string;
    chevronStyles?: string;
    chevronColor?: string;
    error?: boolean;
  }
>(
  (
    {
      children,
      className,
      chevronStyles = 'w-6 min-w-6 h-6 min-h-6 scale-y-[-1]',
      chevronColor = '#6A6A6A',
      error,
      ...props
    },
    ref,
  ) => {
    const { direction } = useLanguage();
    const context = useContext(SelectContext);
    if (!context) throw new Error('SelectTrigger must be used within a Select');

    return (
      <button
        type="button"
        ref={ref}
        className={cn(
          'flex flex-1 items-center justify-between w-full p-4 bg-transparent border border-gray-800 rounded-lg ring-1 ring-gray-800',
          className,
          error && 'border-error text-error ring-error',
        )}
        onClick={() => context.setOpen(!context.open)}
        {...props}
      >
        {children ? children : context.value ? context.value : context.placeholder}
        <ChevronIcon
          className={cn(chevronStyles, direction === 'rtl' ? 'mr-2' : 'ml-2')}
          fill={chevronColor}
        />
      </button>
    );
  },
);

export const SelectValue = ({ placeholder }: { placeholder?: ReactNode }) => {
  const context = useContext(SelectContext);
  if (!context) throw new Error('SelectValue must be used within a Select');

  useEffect(() => {
    context.placeholder = placeholder;
  }, [placeholder, context]);

  return (
    <span className={cn('text-start line-clamp-1 break-words', !context.value && 'text-gray-800')}>
      {context.value || placeholder || ''}
    </span>
  );
};

export const SelectContent = React.forwardRef<
  HTMLDivElement,
  Omit<React.ComponentPropsWithoutRef<'div'>, 'className'> & { className?: string }
>(({ children, className, ...props }, ref) => {
  const { direction } = useLanguage();
  const context = useContext(SelectContext);
  const contentRef = React.useRef<HTMLDivElement | null>(null);

  if (!context) throw new Error('SelectContent must be used within a Select');

  React.useImperativeHandle(ref, () => contentRef.current as HTMLDivElement);

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (contentRef.current && !contentRef.current.contains(event.target as Node)) {
        context.setOpen(false);
      }
    },
    [context],
  );

  useEffect(() => {
    if (context.open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [context.open, handleClickOutside]);

  if (!context.open) return null;

  return (
    <div
      ref={contentRef}
      className={cn(
        'absolute z-10 min-w-48 w-full mt-1 bg-[linear-gradient(144.42deg,#151515_0%,#101010_100%)] border border-gray-800 text-gray-800 rounded-md shadow-sm overflow-y-auto max-h-[200px]',
        className,
        direction === 'rtl' ? 'left-0' : 'right-0',
      )}
      {...props}
    >
      {children}
    </div>
  );
});

export const SelectItem = <T,>({
  value,
  children,
  className,
  ...props
}: React.ComponentPropsWithoutRef<'div'> & {
  value: T;
  children: ReactNode;
  className?: string;
}) => {
  const context = useContext<SelectContextType<T> | null>(SelectContext);
  if (!context) throw new Error('SelectItem must be used within a Select');

  return (
    <div
      className={cn('px-3 py-2 text-sm cursor-pointer hover:text-primary-white-1', className)}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('[data-ignore-select]')) return;
        context.setValue(value);
        context.setOpen(false);
      }}
      {...props}
    >
      {children}
    </div>
  );
};
